import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import CandleChart from '../components/CandleChart';
import { useWebSocket } from '../hooks/useWebSocket';
import { aiApi, AI_WS_URL } from '../api';

const card = { background: '#1e293b', borderRadius: 12, padding: 24, marginBottom: 20 };
const SYMBOLS = ['BTCUSDT', 'ETHUSDT', 'BNBUSDT', 'SOLUSDT'];
const LEVERAGES = [1, 2, 3, 5, 10, 20];

function PnlCell({ value }) {
  const c = value >= 0 ? '#34d399' : '#f87171';
  return <span style={{ color: c, fontWeight: 700 }}>{value >= 0 ? '+' : ''}{value?.toFixed(2)}%</span>;
}

function LiqTag({ price, label }) {
  return (
    <div style={{ background: '#2d1515', border: '1px solid #f87171', borderRadius: 6, padding: '4px 10px', fontSize: 12 }}>
      <span style={{ color: '#f87171' }}>⚠ {label} Liquidação:</span>
      <b style={{ color: '#e2e8f0', marginLeft: 6 }}>${price?.toLocaleString()}</b>
    </div>
  );
}

export default function Futures() {
  const qc = useQueryClient();
  const [symbol, setSymbol] = useState('BTCUSDT');
  const [leverage, setLeverage] = useState(5);
  const [marginPct, setMarginPct] = useState(10);
  const [liveCandle, setLiveCandle] = useState(null);

  const { data: tick } = useWebSocket(`${AI_WS_URL}/ws/prices/${symbol}`);
  const livePrice = tick?.price;

  useEffect(() => {
    if (tick?.closed) setLiveCandle({ time: tick.timestamp, open: tick.open, high: tick.high, low: tick.low, close: tick.price });
  }, [tick]);

  const { data: status } = useQuery({
    queryKey: ['futures-status'],
    queryFn: () => aiApi.get('/futures/status').then(r => r.data),
    refetchInterval: 3_000,
  });

  const { data: analysis } = useQuery({
    queryKey: ['futures-analyze', symbol, leverage],
    queryFn: () => aiApi.get(`/futures/analyze/${symbol}?leverage=${leverage}`).then(r => r.data),
    refetchInterval: 30_000,
  });

  const { data: candles = [] } = useQuery({
    queryKey: ['candles', symbol, '1h'],
    queryFn: () => aiApi.get(`/candles/${symbol}?interval=1h&limit=150`).then(r => r.data),
    staleTime: 60_000,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ['futures-status'] });
  const startMut = useMutation({ mutationFn: () => aiApi.post('/futures/start'), onSuccess: invalidate });
  const stopMut = useMutation({ mutationFn: () => aiApi.post('/futures/stop'), onSuccess: invalidate });
  const resetMut = useMutation({ mutationFn: () => aiApi.post('/futures/reset'), onSuccess: invalidate });

  const openLong = useMutation({
    mutationFn: () => aiApi.post('/futures/order/open', { symbol, side: 'long', price: livePrice || 0, leverage, margin_pct: marginPct }),
    onSuccess: invalidate,
  });
  const openShort = useMutation({
    mutationFn: () => aiApi.post('/futures/order/open', { symbol, side: 'short', price: livePrice || 0, leverage, margin_pct: marginPct }),
    onSuccess: invalidate,
  });
  const closeMut = useMutation({
    mutationFn: (sym) => aiApi.post(`/futures/order/close?symbol=${sym}&price=${livePrice || 0}`),
    onSuccess: invalidate,
  });

  // Build position markers for chart
  const positionMarkers = (status?.positions || []).map(p => ({
    time: Math.floor(new Date(p.opened_at).getTime() / 1000),
    signal: p.side === 'long' ? 'buy' : 'sell',
    confidence: 1,
  }));

  const signalColor = s => s === 'buy' ? '#34d399' : s === 'sell' ? '#f87171' : '#fbbf24';
  const pnlColor = v => v >= 0 ? '#34d399' : '#f87171';

  const liqLong = analysis?.long?.liquidation;
  const liqShort = analysis?.short?.liquidation;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 24, fontWeight: 700 }}>Futuros · Paper Trading</h1>
        {status && (
          <span style={{ background: status.running ? '#052e16' : '#1e293b', color: status.running ? '#34d399' : '#64748b', borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 700 }}>
            {status.running ? '● BOT ON' : '○ MANUAL'}
          </span>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button onClick={() => resetMut.mutate()} style={{ background: '#1e293b', color: '#64748b', border: '1px solid #334155', borderRadius: 8, padding: '7px 14px', cursor: 'pointer', fontSize: 13 }}>Reset</button>
          {status?.running
            ? <button onClick={() => stopMut.mutate()} style={{ background: '#2d1515', color: '#f87171', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 600 }}>Parar Bot</button>
            : <button onClick={() => startMut.mutate()} style={{ background: '#38bdf8', color: '#0f172a', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 600 }}>Iniciar Bot Auto</button>
          }
        </div>
      </div>

      {/* Account summary */}
      {status && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Capital', value: `$${status.capital?.toLocaleString()}` },
            { label: 'Margem livre', value: `$${status.free_margin?.toLocaleString()}` },
            { label: 'Margem bloqueada', value: `$${status.locked_margin?.toLocaleString()}` },
            { label: 'P&L não realizado', value: `${status.unrealized_pnl >= 0 ? '+' : ''}$${status.unrealized_pnl?.toFixed(2)}`, color: pnlColor(status.unrealized_pnl) },
            { label: 'P&L Total', value: `${status.pnl_pct >= 0 ? '+' : ''}${status.pnl_pct?.toFixed(2)}%`, color: pnlColor(status.pnl_pct) },
          ].map(({ label, value, color = '#e2e8f0' }) => (
            <div key={label} style={{ ...card, marginBottom: 0, padding: '12px 16px' }}>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 2 }}>{label}</div>
              <div style={{ fontSize: 17, fontWeight: 700, color }}>{value}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 20 }}>
        {/* Chart */}
        <div style={card}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            {SYMBOLS.map(s => (
              <button key={s} onClick={() => setSymbol(s)} style={{ background: symbol === s ? '#38bdf8' : '#0f172a', color: symbol === s ? '#0f172a' : '#94a3b8', border: '1px solid #334155', borderRadius: 6, padding: '4px 12px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>{s}</button>
            ))}
            {livePrice && <span style={{ marginLeft: 'auto', color: '#38bdf8', fontWeight: 700, fontSize: 16 }}>${livePrice?.toLocaleString()}</span>}
          </div>
          <CandleChart data={candles} liveCandle={liveCandle} markers={positionMarkers} height={300} />
          {/* Liquidation price lines info */}
          {status?.positions?.length > 0 && (
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              {status.positions.map((p, i) => (
                <LiqTag key={i} price={p.liquidation_price} label={p.side === 'long' ? '↑' : '↓'} />
              ))}
            </div>
          )}
        </div>

        {/* Order panel */}
        <div>
          {/* AI Analysis */}
          {analysis && (
            <div style={card}>
              <h2 style={{ fontSize: 14, color: '#94a3b8', marginBottom: 12 }}>Análise IA · {symbol}</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: signalColor(analysis.signal) }}>{analysis.signal?.toUpperCase()}</div>
                <div style={{ fontSize: 13, color: '#64748b' }}>Confiança<br /><b style={{ color: '#e2e8f0' }}>{Math.round((analysis.confidence || 0) * 100)}%</b></div>
              </div>
              <div style={{ fontSize: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {[
                  ['Preço atual', `$${analysis.current_price?.toLocaleString()}`],
                  ['Liq. Long', `$${analysis.long?.liquidation?.toLocaleString()}`],
                  ['Liq. Short', `$${analysis.short?.liquidation?.toLocaleString()}`],
                  ['SL Long (10%)', `$${analysis.long?.stop_loss_10pct?.toLocaleString()}`],
                  ['TP Long (20%)', `$${analysis.long?.take_profit_20pct?.toLocaleString()}`],
                  ['SL Short (10%)', `$${analysis.short?.stop_loss_10pct?.toLocaleString()}`],
                ].map(([k, v]) => (
                  <div key={k}><span style={{ color: '#64748b' }}>{k}: </span><b style={{ color: '#e2e8f0' }}>{v}</b></div>
                ))}
              </div>
            </div>
          )}

          {/* Order form */}
          <div style={card}>
            <h2 style={{ fontSize: 14, color: '#94a3b8', marginBottom: 14 }}>Nova Ordem</h2>
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: '#64748b' }}>Alavancagem</span>
                <b style={{ color: '#fbbf24' }}>{leverage}x</b>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {LEVERAGES.map(l => (
                  <button key={l} onClick={() => setLeverage(l)} style={{ flex: 1, background: leverage === l ? '#fbbf24' : '#0f172a', color: leverage === l ? '#0f172a' : '#64748b', border: '1px solid #334155', borderRadius: 6, padding: '5px 0', cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>{l}x</button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, color: '#64748b' }}>Margem por trade</span>
                <b style={{ color: '#e2e8f0' }}>{marginPct}% = ${((status?.capital || 0) * marginPct / 100).toFixed(0)}</b>
              </div>
              <input type="range" min={1} max={50} value={marginPct} style={{ width: '100%', accentColor: '#38bdf8' }} onChange={e => setMarginPct(Number(e.target.value))} />
            </div>

            {livePrice && (
              <div style={{ background: '#0f172a', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 12, color: '#64748b' }}>
                Posição nominal: <b style={{ color: '#e2e8f0' }}>${((status?.capital || 0) * marginPct / 100 * leverage).toFixed(0)}</b> ({leverage}x)
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <button onClick={() => openLong.mutate()} disabled={openLong.isPending || !livePrice}
                style={{ background: '#052e16', color: '#34d399', border: '1px solid #34d399', borderRadius: 8, padding: '12px', cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
                ▲ LONG
              </button>
              <button onClick={() => openShort.mutate()} disabled={openShort.isPending || !livePrice}
                style={{ background: '#2d1515', color: '#f87171', border: '1px solid #f87171', borderRadius: 8, padding: '12px', cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
                ▼ SHORT
              </button>
            </div>

            <div style={{ marginTop: 12, padding: 10, background: '#0f172a', borderRadius: 8, fontSize: 11, color: '#64748b', lineHeight: 1.8 }}>
              <b style={{ color: '#fbbf24' }}>⚠ Risco com {leverage}x:</b><br />
              Movimento de {(100 / leverage * 0.9).toFixed(1)}% contra você → liquidação<br />
              Stop loss recomendado: {(100 / leverage * 0.3).toFixed(1)}% de preço
            </div>
          </div>
        </div>
      </div>

      {/* Open positions */}
      {status?.positions?.length > 0 && (
        <div style={card}>
          <h2 style={{ fontSize: 15, color: '#94a3b8', marginBottom: 12 }}>Posições Abertas</h2>
          {status.positions.map((p, i) => (
            <div key={i} style={{
              background: '#0f172a', borderRadius: 10, padding: '14px 18px', marginBottom: 10,
              borderLeft: `4px solid ${p.side === 'long' ? '#34d399' : '#f87171'}`,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12,
            }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15 }}>
                  <span style={{ color: p.side === 'long' ? '#34d399' : '#f87171' }}>{p.side === 'long' ? '▲ LONG' : '▼ SHORT'}</span>
                  {' '}{p.symbol} · {p.leverage}x
                </div>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
                  Entrada: <b>${p.entry_price?.toLocaleString()}</b> · Margem: <b>${p.margin?.toLocaleString()}</b>
                </div>
                {p.liquidated && <div style={{ color: '#f87171', fontSize: 12, fontWeight: 700 }}>⚠ LIQUIDADA</div>}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, color: '#64748b' }}>Liq: ${p.liquidation_price?.toLocaleString()}</div>
                {p.pnl !== undefined && (
                  <>
                    <div style={{ color: pnlColor(p.pnl), fontWeight: 700, fontSize: 16 }}>{p.pnl >= 0 ? '+' : ''}${p.pnl?.toFixed(2)}</div>
                    <PnlCell value={p.pnl_pct} />
                  </>
                )}
              </div>
              <button onClick={() => closeMut.mutate(p.symbol)} style={{ background: '#334155', color: '#e2e8f0', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 600 }}>
                Fechar
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Trade history */}
      {status?.recent_trades?.filter(t => t.direction === 'close').length > 0 && (
        <div style={card}>
          <h2 style={{ fontSize: 15, color: '#94a3b8', marginBottom: 12 }}>Histórico</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ color: '#64748b', textAlign: 'left', borderBottom: '1px solid #334155' }}>
                {['Direção', 'Ativo', 'Alavancagem', 'Entrada', 'Saída', 'P&L', 'P&L %', 'Motivo'].map(h => (
                  <th key={h} style={{ padding: '6px 8px 6px 0' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...status.recent_trades].filter(t => t.direction === 'close').reverse().map((t, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #1e293b' }}>
                  <td style={{ padding: '8px 8px 8px 0', color: t.side === 'long' ? '#34d399' : '#f87171', fontWeight: 700 }}>{t.side === 'long' ? '▲ LONG' : '▼ SHORT'}</td>
                  <td>{t.symbol}</td>
                  <td style={{ color: '#fbbf24' }}>{t.leverage}x</td>
                  <td>${t.entry_price?.toLocaleString()}</td>
                  <td>${t.exit_price?.toLocaleString()}</td>
                  <td style={{ color: pnlColor(t.pnl || 0) }}>{t.pnl >= 0 ? '+' : ''}${t.pnl?.toFixed(2)}</td>
                  <td><PnlCell value={t.pnl_pct} /></td>
                  <td style={{ color: '#64748b' }}>{t.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
